import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ScrollText, RotateCcw, LogIn as LogInIcon } from "lucide-react";
import { toast } from "sonner";

const KIND_ICON = { impersonate: LogInIcon };
const KIND_CLS = {
  impersonate: "bg-fuchsia-500/15 text-fuchsia-500 border-fuchsia-400/40",
};

export default function AuditLogs() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/audit-logs", { params: { kind: kind || undefined, limit: 500 } });
      setRows(r.data.results || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load audit logs"); }
    setBusy(false);
  };
  useEffect(() => { load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = rows.filter((r) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (
      (r.actor_email || "").toLowerCase().includes(s) ||
      (r.target_user_email || "").toLowerCase().includes(s) ||
      (r.tenant_name || "").toLowerCase().includes(s) ||
      (r.tenant_slug || "").toLowerCase().includes(s)
    );
  });

  return (
    <div className="p-4">
      <div className="mb-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">§ Super Admin</div>
        <h1 className="font-display font-bold text-2xl mt-1 flex items-center gap-2">
          <ScrollText className="w-5 h-5 text-fuchsia-400" /> Audit logs
        </h1>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email / tenant" className="max-w-xs rounded-sm h-8 text-sm" data-testid="audit-search" />
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="rounded-sm border border-border bg-background px-2 py-1 text-xs h-8" data-testid="audit-kind-filter">
          <option value="">All kinds</option>
          <option value="impersonate">impersonate</option>
        </select>
        <Button variant="outline" size="sm" className="rounded-sm h-8 text-xs" onClick={load} disabled={busy} data-testid="audit-refresh">
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />} Refresh
        </Button>
        <div className="flex-1" />
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          {filtered.length} of {rows.length} entries
        </div>
      </div>

      <div className="panel overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground bg-muted/40">
            <tr>
              <th className="text-left px-3 py-2">When (UTC)</th>
              <th className="text-left px-3 py-2">Kind</th>
              <th className="text-left px-3 py-2">Actor (super admin)</th>
              <th className="text-left px-3 py-2">Target user</th>
              <th className="text-left px-3 py-2">Tenant</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => {
              const Icon = KIND_ICON[r.kind] || ScrollText;
              const cls = KIND_CLS[r.kind] || "bg-muted text-muted-foreground border-border";
              return (
                <tr key={r.id || i} className="border-t border-border" data-testid={`audit-row-${i}`}>
                  <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">
                    {r.at ? new Date(r.at).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <Badge className={`${cls} border text-[10px] font-mono uppercase tracking-widest flex items-center gap-1 w-fit`}>
                      <Icon className="w-2.5 h-2.5" />
                      {r.kind}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-xs">{r.actor_email || <span className="text-muted-foreground">{r.actor_id}</span>}</td>
                  <td className="px-3 py-2 text-xs">{r.target_user_email || <span className="text-muted-foreground">{r.target_user_id || "—"}</span>}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.tenant_name ? (
                      <>
                        <span>{r.tenant_name}</span>
                        <span className="text-muted-foreground font-mono ml-1">({r.tenant_slug})</span>
                      </>
                    ) : <span className="text-muted-foreground font-mono">{r.tenant_id || "—"}</span>}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-sm text-muted-foreground">
                {busy ? "Loading…" : rows.length === 0 ? "No audit entries yet." : "No entries match the current filter."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
